import React, { useCallback, useEffect, useId, useState } from 'react';
import { Helmet } from '@plone/volto/helpers';
import { Link, useLocation } from 'react-router-dom';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import {
  Button,
  Container,
  Form,
  Header,
  Message,
  Segment,
  Table,
} from 'semantic-ui-react';
import {
  createShortUrl,
  deleteShortUrl,
  listShortUrls,
} from './shortUrlsApi';

const messages = defineMessages({
  title: {
    id: 'Short URLs',
    defaultMessage: 'Short URLs',
  },
  description: {
    id: 'shortUrlsDescription',
    defaultMessage:
      'Create short links with hidden UTM parameters for Google Analytics. Share the short URL; tracking parameters are added when someone clicks.',
  },
  longUrl: {
    id: 'Destination URL',
    defaultMessage: 'Destination URL',
  },
  customSlug: {
    id: 'Custom slug (optional)',
    defaultMessage: 'Custom slug (optional)',
  },
  linkTitle: {
    id: 'Title (optional)',
    defaultMessage: 'Title (optional)',
  },
  utmSource: {
    id: 'UTM source',
    defaultMessage: 'UTM source',
  },
  utmMedium: {
    id: 'UTM medium',
    defaultMessage: 'UTM medium',
  },
  utmCampaign: {
    id: 'UTM campaign',
    defaultMessage: 'UTM campaign',
  },
  utmTerm: {
    id: 'UTM term',
    defaultMessage: 'UTM term',
  },
  utmContent: {
    id: 'UTM content',
    defaultMessage: 'UTM content',
  },
  create: {
    id: 'Create short URL',
    defaultMessage: 'Create short URL',
  },
  copy: {
    id: 'Copy',
    defaultMessage: 'Copy',
  },
  copied: {
    id: 'Copied',
    defaultMessage: 'Copied',
  },
  delete: {
    id: 'Delete',
    defaultMessage: 'Delete',
  },
  shortUrl: {
    id: 'Short URL',
    defaultMessage: 'Short URL',
  },
  destination: {
    id: 'Destination',
    defaultMessage: 'Destination',
  },
  visits: {
    id: 'Visits',
    defaultMessage: 'Visits',
  },
  empty: {
    id: 'No short URLs yet.',
    defaultMessage: 'No short URLs yet.',
  },
  confirmDelete: {
    id: 'Delete this short URL?',
    defaultMessage: 'Delete this short URL?',
  },
  loginRequired: {
    id: 'shortUrlsLoginRequired',
    defaultMessage: 'Sign in to manage short URLs.',
  },
  accessDenied: {
    id: 'shortUrlsAccessDenied',
    defaultMessage:
      'You do not have permission to manage short URLs. Contact a site administrator to join the Short URL managers group.',
  },
  signIn: {
    id: 'Sign in',
    defaultMessage: 'Sign in',
  },
});

const emptyForm = {
  longUrl: '',
  customSlug: '',
  title: '',
  utmSource: '',
  utmMedium: '',
  utmCampaign: '',
  utmTerm: '',
  utmContent: '',
};

function isAccessDeniedError(err) {
  const status = err?.response?.status;
  return status === 401 || status === 403;
}

const ShortUrlsView = () => {
  const intl = useIntl();
  const location = useLocation();
  const token = useSelector((state) => state.userSession?.token);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);

  const longUrlId = useId();
  const customSlugId = useId();
  const titleId = useId();
  const utmSourceId = useId();
  const utmMediumId = useId();
  const utmCampaignId = useId();
  const utmTermId = useId();
  const utmContentId = useId();

  const loginHref = `/login?return_url=${encodeURIComponent(
    location.pathname + location.search,
  )}`;

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    setAccessDenied(false);
    try {
      const data = await listShortUrls();
      setItems(data);
    } catch (err) {
      if (isAccessDeniedError(err)) {
        setAccessDenied(true);
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    loadItems();
  }, [token, loadItems]);

  const handleFieldChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const created = await createShortUrl(form);
      setSuccess(created.publicUrl || created.shortUrl);
      setForm(emptyForm);
      await loadItems();
    } catch (err) {
      if (isAccessDeniedError(err)) {
        setAccessDenied(true);
      } else {
        setError(err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async (url, shortCode) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedCode(shortCode);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch {
      setError('Could not copy to clipboard.');
    }
  };

  const handleDelete = async (shortCode) => {
    if (!window.confirm(intl.formatMessage(messages.confirmDelete))) {
      return;
    }
    setError(null);
    try {
      await deleteShortUrl(shortCode);
      await loadItems();
    } catch (err) {
      if (isAccessDeniedError(err)) {
        setAccessDenied(true);
      } else {
        setError(err.message);
      }
    }
  };

  return (
    <div className="view-wrapper">
      <Helmet title={intl.formatMessage(messages.title)} />
      <Container as="main" id="main-content" className="short-urls-view">
        <Header as="h1">{intl.formatMessage(messages.title)}</Header>
        <p>{intl.formatMessage(messages.description)}</p>

        {!token && (
          <Message info role="status">
            <p>{intl.formatMessage(messages.loginRequired)}</p>
            <Button as={Link} to={loginHref} primary>
              {intl.formatMessage(messages.signIn)}
            </Button>
          </Message>
        )}

        {token && accessDenied && (
          <Message negative role="alert">
            {intl.formatMessage(messages.accessDenied)}
          </Message>
        )}

        {token && !accessDenied && (
          <>
            {error && (
              <Message negative role="alert">
                {error}
              </Message>
            )}
            {success && (
              <Message positive role="status">
                <FormattedMessage
                  id="Short URL created"
                  defaultMessage="Short URL created: {url}"
                  values={{ url: success }}
                />
              </Message>
            )}
            <Segment attached="bottom">
              <Form onSubmit={handleSubmit}>
                <Form.Field required>
                  <label htmlFor={longUrlId}>
                    {intl.formatMessage(messages.longUrl)}
                  </label>
                  <input
                    id={longUrlId}
                    type="url"
                    required
                    value={form.longUrl}
                    onChange={handleFieldChange('longUrl')}
                    placeholder="https://www.unite-university.eu/your-page"
                  />
                </Form.Field>
                <Form.Group widths="equal">
                  <Form.Field>
                    <label htmlFor={customSlugId}>
                      {intl.formatMessage(messages.customSlug)}
                    </label>
                    <input
                      id={customSlugId}
                      type="text"
                      value={form.customSlug}
                      onChange={handleFieldChange('customSlug')}
                      placeholder="master-promo"
                    />
                  </Form.Field>
                  <Form.Field>
                    <label htmlFor={titleId}>
                      {intl.formatMessage(messages.linkTitle)}
                    </label>
                    <input
                      id={titleId}
                      type="text"
                      value={form.title}
                      onChange={handleFieldChange('title')}
                    />
                  </Form.Field>
                </Form.Group>
                <Header as="h2" size="small">
                  UTM parameters
                </Header>
                <Form.Group widths="equal">
                  <Form.Field>
                    <label htmlFor={utmSourceId}>
                      {intl.formatMessage(messages.utmSource)}
                    </label>
                    <input
                      id={utmSourceId}
                      type="text"
                      value={form.utmSource}
                      onChange={handleFieldChange('utmSource')}
                    />
                  </Form.Field>
                  <Form.Field>
                    <label htmlFor={utmMediumId}>
                      {intl.formatMessage(messages.utmMedium)}
                    </label>
                    <input
                      id={utmMediumId}
                      type="text"
                      value={form.utmMedium}
                      onChange={handleFieldChange('utmMedium')}
                    />
                  </Form.Field>
                  <Form.Field>
                    <label htmlFor={utmCampaignId}>
                      {intl.formatMessage(messages.utmCampaign)}
                    </label>
                    <input
                      id={utmCampaignId}
                      type="text"
                      value={form.utmCampaign}
                      onChange={handleFieldChange('utmCampaign')}
                    />
                  </Form.Field>
                </Form.Group>
                <Form.Group widths="equal">
                  <Form.Field>
                    <label htmlFor={utmTermId}>
                      {intl.formatMessage(messages.utmTerm)}
                    </label>
                    <input
                      id={utmTermId}
                      type="text"
                      value={form.utmTerm}
                      onChange={handleFieldChange('utmTerm')}
                    />
                  </Form.Field>
                  <Form.Field>
                    <label htmlFor={utmContentId}>
                      {intl.formatMessage(messages.utmContent)}
                    </label>
                    <input
                      id={utmContentId}
                      type="text"
                      value={form.utmContent}
                      onChange={handleFieldChange('utmContent')}
                    />
                  </Form.Field>
                </Form.Group>
                <Button
                  primary
                  type="submit"
                  loading={submitting}
                  disabled={submitting}
                >
                  {intl.formatMessage(messages.create)}
                </Button>
              </Form>
            </Segment>
            <Segment attached="bottom">
              <Header as="h2" size="small">
                <FormattedMessage {...messages.title} />
              </Header>
              {loading ? (
                <p>
                  <FormattedMessage
                    id="Loading..."
                    defaultMessage="Loading..."
                  />
                </p>
              ) : items.length === 0 ? (
                <p>{intl.formatMessage(messages.empty)}</p>
              ) : (
                <Table celled striped>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>
                        {intl.formatMessage(messages.shortUrl)}
                      </Table.HeaderCell>
                      <Table.HeaderCell>
                        {intl.formatMessage(messages.destination)}
                      </Table.HeaderCell>
                      <Table.HeaderCell>
                        {intl.formatMessage(messages.visits)}
                      </Table.HeaderCell>
                      <Table.HeaderCell>
                        <FormattedMessage
                          id="Actions"
                          defaultMessage="Actions"
                        />
                      </Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {items.map((item) => {
                      const publicUrl = item.publicUrl || item.shortUrl;
                      return (
                        <Table.Row key={item.shortCode}>
                          <Table.Cell>
                            <a href={publicUrl}>{publicUrl}</a>
                          </Table.Cell>
                          <Table.Cell>{item.longUrl}</Table.Cell>
                          <Table.Cell>
                            {item.visitsSummary?.total ?? 0}
                          </Table.Cell>
                          <Table.Cell>
                            <Button
                              type="button"
                              size="small"
                              onClick={() =>
                                handleCopy(publicUrl, item.shortCode)
                              }
                              aria-label={`${intl.formatMessage(messages.copy)} ${publicUrl}`}
                            >
                              {copiedCode === item.shortCode
                                ? intl.formatMessage(messages.copied)
                                : intl.formatMessage(messages.copy)}
                            </Button>
                            <Button
                              type="button"
                              size="small"
                              negative
                              onClick={() => handleDelete(item.shortCode)}
                              aria-label={`${intl.formatMessage(messages.delete)} ${item.shortCode}`}
                            >
                              {intl.formatMessage(messages.delete)}
                            </Button>
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                  </Table.Body>
                </Table>
              )}
            </Segment>
          </>
        )}
      </Container>
    </div>
  );
};

export default ShortUrlsView;
